import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, ArrowLeft, Check, Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";


const GoogleButton = () => {
  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      type="button"
      onClick={handleGoogle}
      data-testid="google-signin-btn"
      className="w-full inline-flex items-center justify-center gap-3 rounded-full border border-slate-300 bg-white px-6 py-3.5 text-sm font-semibold text-nova-navy hover:border-nova-blue/40 hover:bg-nova-mist transition-colors duration-300"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z"/>
        <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.87-3c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24z"/>
        <path fill="#FBBC05" d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28v-3.1H1.29a12 12 0 0 0 0 10.76l3.98-3.1z"/>
        <path fill="#EA4335" d="M12 4.76c1.76 0 3.34.6 4.58 1.8l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.29 6.62l3.98 3.1C6.22 6.87 8.87 4.76 12 4.76z"/>
      </svg>
      Sign in with Google
    </button>
  );
};

const PERKS = [
  "Connect the tools you already use",
  "Built-in operations from day one",
  "Nova handles the busywork",
];

export default function AuthPage() {
  const { setUser, API } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(location.state?.authError || "");
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryReason = params.get("reason");
    const resetSuccess = params.get("reset") === "success";
    let storedReason = null;
    let notedMinutes = null;
    try {
      storedReason = sessionStorage.getItem("nova_inactivity_notice");
      sessionStorage.removeItem("nova_inactivity_notice");
      notedMinutes = sessionStorage.getItem("nova_inactivity_notice_minutes");
      sessionStorage.removeItem("nova_inactivity_notice_minutes");
    } catch (err) {
      console.warn("AuthPage: could not read logout notice", err);
    }
    const reason = queryReason || storedReason;
    if (resetSuccess) {
      setNotice("Your password has been reset. Please sign in with your new password.");
    } else if (reason === "inactivity") {
      const minutesLabel = notedMinutes ? `${notedMinutes} minutes` : "a period";
      setNotice(`You were signed out after ${minutesLabel} of inactivity. Please sign in again to continue.`);
    } else if (reason === "session_expired") {
      setNotice("Your session ended. Please sign in again to continue.");
    }
  }, [location.search]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      if (data.is_platform_admin) {
        navigate("/admin/overview", { replace: true });
      } else {
        navigate(data.onboarded ? "/dashboard" : "/onboarding", { replace: true });
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white grid lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:flex relative flex-col justify-between bg-[#020305] text-white p-12 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full bg-nova-blue/20 blur-3xl" />
        <div className="absolute inset-0 bg-grid-navy opacity-[0.15]" />
        <div className="relative">
          <Link to="/" data-testid="auth-back-home" className="inline-flex items-center">
            <img
              src="/nova-logo-lockup-dark.png"
              alt="NOVA OPSIS — A New Way Forward."
              className="h-20 lg:h-24 xl:h-28 w-auto select-none"
              draggable={false}
            />
          </Link>
        </div>
        <div className="relative">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="text-4xl xl:text-5xl font-extrabold tracking-tighter leading-[1.05]"
          >
            Run Your Business.
            <br />
            <span className="text-blue-400">Nova Handles the Rest.</span>
          </motion.h2>
          <ul className="mt-10 space-y-4">
            {PERKS.map((p, i) => (
              <motion.li
                key={p}
                initial={{ opacity: 0, x: -14 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.12, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="flex items-center gap-3 text-slate-300"
              >
                <span className="w-6 h-6 rounded-full bg-nova-blue/25 flex items-center justify-center shrink-0">
                  <Check size={12} strokeWidth={3} className="text-blue-300" />
                </span>
                <span className="text-sm font-medium">{p}</span>
              </motion.li>
            ))}
          </ul>
        </div>
        <p className="relative text-[10px] font-semibold tracking-[0.32em] text-blue-400">
          A NEW WAY FORWARD.
        </p>
      </div>

      <div className="flex items-center justify-center px-5 sm:px-10 py-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md"
        >
          <Link
            to="/"
            data-testid="auth-back-to-site"
            className="inline-flex items-center gap-2 text-sm font-medium text-nova-muted hover:text-nova-navy transition-colors mb-8"
          >
            <ArrowLeft size={15} />
            Back to site
          </Link>

          <h1 className="text-3xl font-bold tracking-tighter text-nova-navy">Welcome back</h1>
          <p className="mt-2 text-sm text-nova-slate">
            Sign in to your Nova workspace.
          </p>

          {notice && (
            <p
              data-testid="inactivity-notice"
              className="mt-5 text-sm font-medium text-nova-blue bg-nova-mist border border-nova-bluelight rounded-xl px-4 py-3"
            >
              {notice}
            </p>
          )}

          <div className="mt-8">
            <GoogleButton />
          </div>

          <div className="my-6 flex items-center gap-4">
            <span className="h-px flex-1 bg-slate-200" />
            <span className="text-xs font-medium text-nova-muted">or with email</span>
            <span className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label htmlFor="auth-email" className="block text-xs font-semibold text-nova-navy mb-1.5">
                Work email
              </label>
              <input
                id="auth-email"
                data-testid="auth-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourbusiness.com"
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-nova-navy placeholder:text-nova-muted focus:outline-none focus:border-nova-blue focus:ring-2 focus:ring-nova-blue/15 transition"
              />
            </div>
            <div>
              <label htmlFor="auth-password" className="block text-xs font-semibold text-nova-navy mb-1.5">
                Password
              </label>
              <input
                id="auth-password"
                data-testid="auth-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-nova-navy placeholder:text-nova-muted focus:outline-none focus:border-nova-blue focus:ring-2 focus:ring-nova-blue/15 transition"
              />
              <div className="mt-2 text-right">
                <Link to="/forgot-password" data-testid="auth-forgot-password-link" className="text-xs font-semibold text-nova-blue hover:text-nova-bluedark">
                  Forgot password?
                </Link>
              </div>
            </div>

            {error && (
              <p data-testid="auth-error" className="text-sm font-medium text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                {error}
              </p>
            )}

            <motion.button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-btn"
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-nova-blue px-6 py-3.5 text-sm font-semibold text-white shadow-[0_10px_28px_rgb(37,99,235,0.35)] hover:bg-nova-bluedark transition-colors duration-300 disabled:opacity-60"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              Sign in
              {!loading && <ArrowRight size={15} />}
            </motion.button>
          </form>

          <p className="mt-6 text-sm text-nova-slate text-center">
            New to Nova Opsis?{" "}
            <Link
              to="/request-demo"
              data-testid="auth-request-demo-link"
              className="font-semibold text-nova-blue hover:text-nova-bluedark"
            >
              Request a demo
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
